import { useState } from 'react';

const steps = [
  {
    icon: 'ri-folder-received-line',
    title: '资料归集',
    deliverable: '缺资料清单',
    color: 'from-[#14B8A6] to-[#0D9488]',
  },
  {
    icon: 'ri-shield-check-line',
    title: '提交前校验',
    deliverable: '校验报告',
    color: 'from-[#0D9488] to-[#14B8A6]',
  },
  {
    icon: 'ri-file-list-3-line',
    title: '差异留痕',
    deliverable: '三流差异记录',
    color: 'from-[#14B8A6] to-[#0D9488]',
  },
  {
    icon: 'ri-archive-line',
    title: '单证归档',
    deliverable: '归档目录（可导出）',
    color: 'from-[#0D9488] to-[#14B8A6]',
  },
  {
    icon: 'ri-links-line',
    title: '核销链路',
    deliverable: '一票一链路结构',
    color: 'from-[#14B8A6] to-[#0D9488]',
  },
];

interface ExampleModalProps {
  open: boolean;
  onClose: () => void;
}

function ExampleModal({ open, onClose }: ExampleModalProps) {
  const [activeTab, setActiveTab] = useState(0);

  const examples = [
    {
      title: '缺资料清单',
      content: '按批次/票号展示缺失的资料项，包括报关单、发票、物流单据等，支持批量补充上传。',
    },
    {
      title: '校验报告',
      content: '提交前自动校验数据完整性、格式规范性、逻辑一致性，标红异常项并提供修正建议。',
    },
    {
      title: '三流差异记录',
      content: '自动比对资金流、物流、信息流，标记差异项并记录调整过程，确保可追溯。',
    },
    {
      title: '归档目录',
      content: '按票号/批次自动生成归档目录，支持在线预览、批量下载、长期保存。',
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-800">交付物示例</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 overflow-x-auto">
          {examples.map((example, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeTab === index
                  ? 'bg-[#14B8A6] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {example.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-8">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <h4 className="text-lg font-bold text-gray-800 mb-4">{examples[activeTab].title}</h4>
            <p className="text-gray-600 leading-relaxed mb-6">{examples[activeTab].content}</p>
            
            {/* Mock Preview */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-file-text-line text-[#14B8A6] text-xl"></i>
                <span className="text-sm font-medium text-gray-700">示例文档预览</span>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-3 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ComplianceLoopSection() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">合规管理闭环</h2>
            <p className="text-lg text-gray-600">从数据到证据链，全流程可追溯</p>
          </div>

          {/* Flow Steps */}
          <div className="relative">
            {/* Connection Lines */}
            <div className="absolute top-[72px] left-0 right-0 h-[2px] bg-gradient-to-r from-[#14B8A6]/20 via-[#14B8A6]/40 to-[#14B8A6]/20 hidden lg:block"></div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 relative">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Step Card */}
                  <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#14B8A6] transition-all hover:shadow-lg group">
                    {/* Icon */}
                    <div className="relative mb-4">
                      <div
                        className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform`}
                      >
                        <i className={`${step.icon} text-3xl text-white`}></i>
                      </div>
                      {/* Step Number */}
                      <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#FF6B35] rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                        {index + 1}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-800 mb-3 text-center">{step.title}</h3>

                    {/* Deliverable */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">交付物：</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{step.deliverable}</p>
                    </div>
                  </div>

                  {/* Arrow (desktop only) */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:flex absolute top-[72px] -right-4 w-8 h-8 items-center justify-center z-10">
                      <i className="ri-arrow-right-line text-2xl text-[#14B8A6]"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 h-12 bg-white border-2 border-[#14B8A6] text-[#14B8A6] font-medium rounded-full hover:bg-[#14B8A6] hover:text-white transition-all whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md"
            >
              <i className="ri-eye-line text-lg"></i>
              查看示例
            </button>
          </div>
        </div>
      </section>

      {/* Example Modal */}
      <ExampleModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}